import React, { useEffect, useState, useMemo } from "react";
import Swal from "sweetalert2";
import {
    collection,
    doc,
    getDoc,
    onSnapshot,
    query,
    where,
    getDocs
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import Menu from "../../panel/Menu";
import moment from "moment";

import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Chip,
    LinearProgress
} from "@mui/material";

function Checkoutreport() {
    const [bookings, setBookings] = useState([]);
    const [propertyId, setPropertyId] = useState(null);
    const [username, setUsername] = useState("");
    const [searchText, setSearchText] = useState("");
    const [loading, setLoading] = useState(true);

    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
    });

    // ================= STEP 1: AUTH & PROPERTY IDENTIFICATION =================
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const docRef = doc(db, "authroles", user.uid);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                        setUsername(docSnap.data().name);
                        setPropertyId(docSnap.data().propertyId);
                    }
                } catch (error) {
                    console.error("Auth Fetch Error:", error);
                }
            } else {
                setLoading(false);
            }
        });
        return () => unsub();
    }, []);

    // ================= STEP 2: REAL-TIME DATA LISTENER =================
    useEffect(() => {
        if (!propertyId) return;

        const today = moment().format("YYYY-MM-DD");

        // Optimized Query: Only fetch what is needed for TODAY'S checkouts
        const q = query(
            collection(db, "reservations"),
            where("propertyId", "==", propertyId),
            where("checkoutdate", "==", today),
            where("status", "==", "Booked")
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setBookings(data);
            setLoading(false);
        }, (error) => {
            Toast.fire({ icon: "error", title: "Failed to load checkouts" });
            setLoading(false);
        });

        return () => unsubscribe();
    }, [propertyId]);

    // ================= STEP 3: OPTIMIZED SEARCH (Local Filtering) =================
    // useMemo prevents unnecessary re-calculations on every render
    const filteredBookings = useMemo(() => {
        return bookings.filter((b) =>
            b.customers?.custname?.toLowerCase().includes(searchText.toLowerCase()) ||
            b.customers?.mobile?.includes(searchText)
        );
    }, [searchText, bookings]);

    return (
        <Box >
            <Menu />

            <Container className="content-wrapper" maxWidth="xl" sx={{ p: 4 }}>
                {/* HEADER SECTION */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "#0a2540" }}>
                            Checkout Alerts
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Guests scheduled to depart today: {moment().format("DD MMM YYYY")}
                        </Typography>
                    </Box>
                    <Chip
                        label={`Staff: ${username || 'Loading...'}`}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                    />
                </Box>

                <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", border: "none" }}>
                    {loading && <LinearProgress color="secondary" />}
                    <CardContent sx={{ p: 3 }}>

                        {/* SEARCH & FILTERS */}
                        <TextField
                            placeholder="Search by name or mobile..."
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{ mb: 3, maxWidth: 450, backgroundColor: "#fff" }}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />

                        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #eee", borderRadius: 2 }}>
                            <Table size="medium">
                                <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Sr</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Customer Details</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Stay Period</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Room & Plan</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }} align="right">Financial Status</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {filteredBookings.length > 0 ? (
                                        filteredBookings.map((item, index) => (
                                            <TableRow key={item.id} hover>
                                                <TableCell>{index + 1}</TableCell>

                                                <TableCell>
                                                    <Typography variant="subtitle2" sx={{ textTransform: "capitalize", fontWeight: 700 }}>
                                                        {item.customers?.custname}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {item.customers?.mobile}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {moment(item.checkindate).format("DD/MM")} - {moment(item.checkoutdate).format("DD/MM")}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: "orange", fontWeight: 700 }}>
                                                        {item.adults} Adults
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    {item.rooms?.map((r, i) => (
                                                        <Chip
                                                            key={i}
                                                            label={`${r.roomno} (${r.planname})`}
                                                            size="small"
                                                            sx={{ m: 0.2, fontSize: '0.7rem' }}
                                                        />
                                                    ))}
                                                </TableCell>

                                                <TableCell align="right">
                                                    <Box>
                                                        <Typography variant="body2">Total: ₹{item.totalamount?.toFixed(2)}</Typography>
                                                        <Typography variant="caption" color="success.main">Paid: ₹{item.advance?.toFixed(2)}</Typography>
                                                        <Typography variant="subtitle2" sx={{ color: item.remaining > 0 ? "error.main" : "success.main", fontWeight: 800 }}>
                                                            Due: ₹{item.remaining?.toFixed(2)}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                                <Typography color="textSecondary">
                                                    {loading ? "Fetching data..." : "No checkouts found for today."}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
}

export default Checkoutreport;